import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { authService } from '../../services/auth';
import { useUser } from '../../contexts/UserContext';
import SignInModal from './SignInModal';

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 50vh;
  color: #ffffff;
  font-size: 1.2rem;
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.8);
  z-index: 1000;
  display: flex;
  justify-content: center;
  align-items: center;
`;

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiresPermission?: 'post_blog' | 'can_create' | 'is_admin';
}

/**
 * ProtectedRoute Component - Wraps components that require authentication
 * 
 * This component:
 * - Checks if user is authenticated
 * - Optionally checks if user has required permissions
 * - If authenticated: renders the protected content
 * - If not authenticated: shows login modal over a dark overlay
 * - If authenticated but lacks permission: shows permission denied message
 * - If modal is closed without login: redirects to home page
 * - After successful login: renders the protected content
 */
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, requiresPermission }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const { user } = useUser();
  const navigate = useNavigate();

  useEffect(() => {
    const checkAuth = () => {
      const isLoggedIn = authService.isAuthenticated();
      setIsAuthenticated(isLoggedIn);
      
      if (!isLoggedIn) {
        setShowLoginModal(true);
      }
    };

    checkAuth();
  }, []);

  const handleLoginSuccess = () => {
    setIsAuthenticated(true);
    setShowLoginModal(false);
  };

  const handleLoginClose = () => {
    // If user closes modal without logging in, redirect to home
    setShowLoginModal(false);
    navigate('/');
  };

  // Loading state while checking authentication
  if (isAuthenticated === null) {
    return <LoadingContainer>Loading...</LoadingContainer>;
  }

  // If authenticated, check permissions if required
  if (isAuthenticated) {
    if (requiresPermission && user) {
      // Note: Database stores as "admin" but TypeScript interface uses is_admin
      const hasPermission = user.user_permissions?.[requiresPermission] || (user.user_permissions as any)?.admin || user.user_permissions?.is_admin;
      
      if (!hasPermission) {
        return (
          <LoadingContainer>
            You don't have permission to access this page.
          </LoadingContainer>
        );
      }
    }
    
    return <>{children}</>;
  }

  // If not authenticated, show login modal with overlay
  return (
    <ModalOverlay>
      <SignInModal
        isOpen={showLoginModal}
        onClose={handleLoginClose}
        onSuccess={handleLoginSuccess}
      />
    </ModalOverlay>
  );
};

export default ProtectedRoute; 